import Route from '@ember/routing/route'
import { service } from '@ember/service'
import type ReportingService from 'teacher-ui-client/services/reporting'
import { restartableTask, timeout } from 'ember-concurrency'
import { Product } from 'teacher-ui-client/utils/products'
import { toRequestScopeStudent } from 'teacher-ui-client/services/reporting'
import { ExportType } from 'teacher-ui-client/components/reporting/export-select'
import type {
  RequestScope,
  BaseReport,
  BaseReportMetaPaging,
  ReportingQueryPage,
  ReportingQuerySort,
  ReportingQueryFilter,
} from 'teacher-ui-client/services/reporting'
import type ReportingUiScopeRoute from 'teacher-ui-client/reporting/ui-scope/route'
import type ReadingEggspressDashboardStudentRoute from 'teacher-ui-client/reporting/ui-scope/rex/dashboard/student/route'
import type { NormalizedInterval } from 'date-fns'

export interface ComprehensionProgressOverviewReportData extends BaseReport {
  overview: {
    initial_lesson: Nullable<number>
    highest_lesson_completed: Nullable<number>
    total_lessons_completed: number
    number_of_quizzes_taken: number
    average_quiz_score: Nullable<number>
    total_attempts: number
  }
}

export interface ComprehensionProgressPlacementEvent {
  created_at: ISO8601DateTime
  lesson: number
  lesson_name: string
  placement: true
}

export interface ComprehensionProgressLessonEvent {
  created_at: ISO8601DateTime
  lesson: number
  lesson_name: string
  placement: false
  score: number
  number_of_attempts: number
}

export interface ComprehensionProgressEventsReportData extends BaseReport {
  events: (ComprehensionProgressLessonEvent | ComprehensionProgressPlacementEvent)[]
  meta: BaseReportMetaPaging
}

export interface LexileGrowthOverviewReportData extends BaseReport {
  overview: {
    highest_lexile_completed: number
    lexile_growth: number
  }
}

export interface ProgressByCategoryOverviewReportData extends BaseReport {
  overview: {
    categories: {
      literal: {
        questions_answered: Nullable<number>
        average_score: Nullable<number>
      }
      inferential: {
        questions_answered: Nullable<number>
        average_score: Nullable<number>
      }
      vocabulary_and_usage: {
        questions_answered: Nullable<number>
        average_score: Nullable<number>
      }
      text_analysis_and_critical_literacy: {
        questions_answered: Nullable<number>
        average_score: Nullable<number>
      }
    }
  }
}

export interface AssessmentsOverviewReportData extends BaseReport {
  overview: {
    total_assessments_completed: number
    average_assessment_score: Nullable<number>
  }
}

export interface AssessmentsEventsReportData extends BaseReport {
  events: {
    created_at: ISO8601DateTime
    classification: 'fiction' | 'nonfiction'
    lexile: number
    score: number
  }[]
  meta: BaseReportMetaPaging
}

export interface AssignmentsOverviewReportData extends BaseReport {
  overview: {
    total_assignments_completed: number
    average_assignment_score: number
  }
}

export interface AssignmentsEventsReportData extends BaseReport {
  events: {
    created_at: ISO8601DateTime
    task_type: 'book' | 'reading' | 'spelling'
    task_name: string
    task_chapter?: Nullable<number>
    task_lesson?: string
    score: number
  }[]
  meta: BaseReportMetaPaging
}

export interface SpellingProgressOverviewReportData extends BaseReport {
  overview: {
    highest_lesson_completed: Nullable<number>
    total_lessons_completed: number
    average_overall_score: Nullable<number>
  }
}

export interface SpellingProgressEventsReportData extends BaseReport {
  events: {
    created_at: ISO8601DateTime
    lesson: number
    lesson_name: string
    /**
     * Provides a string like "2.34" which is a "grade.grade_lesson_number"
     * The events currently reported by rex-client have an incorrect grade, so our BE calculates its own value here and
     * reports that to TUI
     */
    lesson_module: string
    /**
     * Should be a value 1, 2 or 3. 3 being the most difficult
     */
    achievement_level: number
    score: number
  }[]
  meta: BaseReportMetaPaging
}

export interface AwardsOverviewReportData extends BaseReport {
  overview: {
    gold: number
    silver: number
    bronze: number
  }
}

export interface StadiumOverviewReportData extends BaseReport {
  overview: {
    game_types: {
      spelling: {
        events_completed: Nullable<number>
        correct_answers: Nullable<number>
        accuracy_percentage: Nullable<number>
        highest_score: Nullable<number>
      }
      grammar: {
        events_completed: Nullable<number>
        correct_answers: Nullable<number>
        accuracy_percentage: Nullable<number>
        highest_score: Nullable<number>
      }
      vocabulary: {
        events_completed: Nullable<number>
        correct_answers: Nullable<number>
        accuracy_percentage: Nullable<number>
        highest_score: Nullable<number>
      }
      usage: {
        events_completed: Nullable<number>
        correct_answers: Nullable<number>
        accuracy_percentage: Nullable<number>
        highest_score: Nullable<number>
      }
    }
  }
}

export interface BooksReadOverviewReportData extends BaseReport {
  overview: {
    classifications: {
      fiction: {
        books_read: number
        chapters_read: number
        days_read: number
        words_read: number
        questions: number
        average_score: number
      }
      nonfiction: {
        books_read: number
        chapters_read: number
        days_read: number
        words_read: number
        questions: number
        average_score: number
      }
    }
    total: {
      books_read: number
      chapters_read: number
      words_read: number
      questions: number
      // NOTE: average_score, days_read intentionally missing
    }
  }
}

export interface BooksReadEventsReportData extends BaseReport {
  events: {
    created_at: ISO8601DateTime
    book_name: string
    chapter: Nullable<number> // null when it was chapter-less book that was read
    classification: 'fiction' | 'nonfiction'
    lexile_formatted: string
  }[]
  meta: BaseReportMetaPaging
}

export default class RexDashboardStudentIndexRoute extends Route {
  @service reporting!: ReportingService

  buildRouteInfoMetadata() {
    return {
      exportOptions: [{ exportType: ExportType.CSV }, { exportType: ExportType.print }],
    }
  }

  model() {
    const { period } = this.modelFor('reporting.ui-scope') as ModelFor<ReportingUiScopeRoute>

    const { student } = this.modelFor(
      'reporting.ui-scope.rex.dashboard.student',
    ) as ModelFor<ReadingEggspressDashboardStudentRoute>
    const requestScope = toRequestScopeStudent(student.id)

    // Events shown on this page are always latest 5
    const eventSort = '-created_at'
    const eventPage = { number: 1, size: 5 }

    void this.fetchComprehensionProgressOverviewReportTask.perform(Product.REX, period, requestScope).catch(() => {})
    void this.fetchComprehensionProgressPlacementEventsReportTask
      .perform(Product.REX, eventSort, eventPage, { placement: true }, period, requestScope)
      .catch(() => {})
    void this.fetchComprehensionProgressEventsReportTask
      .perform(Product.REX, eventSort, eventPage, { placement: false }, period, requestScope)
      .catch(() => {})
    void this.fetchLexileGrowthOverviewReportTask.perform(Product.REX, period, requestScope).catch(() => {})
    void this.fetchProgressByCategoryOverviewReportTask.perform(Product.REX, period, requestScope).catch(() => {})
    void this.fetchAssessmentsOverviewReportTask.perform(Product.REX, period, requestScope).catch(() => {})
    void this.fetchAssessmentsEventsReportTask
      .perform(Product.REX, eventSort, eventPage, period, requestScope)
      .catch(() => {})
    void this.fetchAssignmentsOverviewReportTask.perform(Product.REX, period, requestScope).catch(() => {})
    void this.fetchAssignmentsEventsReportTask
      .perform(Product.REX, eventSort, eventPage, period, requestScope)
      .catch(() => {})
    void this.fetchSpellingProgressOverviewReportTask.perform(Product.REX, period, requestScope).catch(() => {})
    void this.fetchSpellingProgressEventsReportTask
      .perform(Product.REX, eventSort, eventPage, period, requestScope)
      .catch(() => {})
    void this.fetchAwardsOverviewReportTask.perform(Product.REX, period, requestScope).catch(() => {})
    void this.fetchStadiumOverviewReportTask.perform(Product.REX, period, requestScope).catch(() => {})
    void this.fetchBooksReadOverviewReportTask.perform(Product.REX, period, requestScope).catch(() => {})
    void this.fetchBooksReadEventsReportTask
      .perform(Product.REX, eventSort, eventPage, period, requestScope)
      .catch(() => {})

    return {
      student,
      period,
      fetchComprehensionProgressOverviewReportTask: this.fetchComprehensionProgressOverviewReportTask,
      fetchComprehensionProgressPlacementEventsReportTask: this.fetchComprehensionProgressPlacementEventsReportTask,
      fetchComprehensionProgressEventsReportTask: this.fetchComprehensionProgressEventsReportTask,
      fetchLexileGrowthOverviewReportTask: this.fetchLexileGrowthOverviewReportTask,
      fetchProgressByCategoryOverviewReportTask: this.fetchProgressByCategoryOverviewReportTask,
      fetchAssessmentsOverviewReportTask: this.fetchAssessmentsOverviewReportTask,
      fetchAssessmentsEventsReportTask: this.fetchAssessmentsEventsReportTask,
      fetchAssignmentsOverviewReportTask: this.fetchAssignmentsOverviewReportTask,
      fetchAssignmentsEventsReportTask: this.fetchAssignmentsEventsReportTask,
      fetchSpellingProgressOverviewReportTask: this.fetchSpellingProgressOverviewReportTask,
      fetchSpellingProgressEventsReportTask: this.fetchSpellingProgressEventsReportTask,
      fetchAwardsOverviewReportTask: this.fetchAwardsOverviewReportTask,
      fetchStadiumOverviewReportTask: this.fetchStadiumOverviewReportTask,
      fetchBooksReadOverviewReportTask: this.fetchBooksReadOverviewReportTask,
      fetchBooksReadEventsReportTask: this.fetchBooksReadEventsReportTask,
    }
  }

  fetchComprehensionProgressOverviewReportTask = restartableTask(
    async (...args: Parameters<typeof this.fetchComprehensionProgressOverviewReport>) => {
      await timeout(200)
      return this.fetchComprehensionProgressOverviewReport(...args)
    },
  )

  fetchComprehensionProgressPlacementEventsReportTask = restartableTask(
    async (...args: Parameters<typeof this.fetchComprehensionProgressEventsReport>) => {
      await timeout(200)
      return this.fetchComprehensionProgressEventsReport(...args)
    },
  )

  fetchComprehensionProgressEventsReportTask = restartableTask(
    async (...args: Parameters<typeof this.fetchComprehensionProgressEventsReport>) => {
      await timeout(200)
      return this.fetchComprehensionProgressEventsReport(...args)
    },
  )

  fetchLexileGrowthOverviewReportTask = restartableTask(
    async (...args: Parameters<typeof this.fetchLexileGrowthOverviewReport>) => {
      await timeout(200)
      return this.fetchLexileGrowthOverviewReport(...args)
    },
  )

  fetchProgressByCategoryOverviewReportTask = restartableTask(
    async (...args: Parameters<typeof this.fetchProgressByCategoryOverviewReport>) => {
      await timeout(200)
      return this.fetchProgressByCategoryOverviewReport(...args)
    },
  )

  fetchAssessmentsOverviewReportTask = restartableTask(
    async (...args: Parameters<typeof this.fetchAssessmentsOverviewReport>) => {
      await timeout(200)
      return this.fetchAssessmentsOverviewReport(...args)
    },
  )

  fetchAssessmentsEventsReportTask = restartableTask(
    async (...args: Parameters<typeof this.fetchAssessmentsEventsReport>) => {
      await timeout(200)
      return this.fetchAssessmentsEventsReport(...args)
    },
  )

  fetchAssignmentsOverviewReportTask = restartableTask(
    async (...args: Parameters<typeof this.fetchAssignmentsOverviewReport>) => {
      await timeout(200)
      return this.fetchAssignmentsOverviewReport(...args)
    },
  )

  fetchAssignmentsEventsReportTask = restartableTask(
    async (...args: Parameters<typeof this.fetchAssignmentsEventsReport>) => {
      await timeout(200)
      return this.fetchAssignmentsEventsReport(...args)
    },
  )

  fetchSpellingProgressOverviewReportTask = restartableTask(
    async (...args: Parameters<typeof this.fetchSpellingProgressOverviewReport>) => {
      await timeout(200)
      return this.fetchSpellingProgressOverviewReport(...args)
    },
  )

  fetchSpellingProgressEventsReportTask = restartableTask(
    async (...args: Parameters<typeof this.fetchSpellingProgressEventsReport>) => {
      await timeout(200)
      return this.fetchSpellingProgressEventsReport(...args)
    },
  )

  fetchAwardsOverviewReportTask = restartableTask(
    async (...args: Parameters<typeof this.fetchAwardsOverviewReport>) => {
      await timeout(200)
      return this.fetchAwardsOverviewReport(...args)
    },
  )

  fetchStadiumOverviewReportTask = restartableTask(
    async (...args: Parameters<typeof this.fetchStadiumOverviewReport>) => {
      await timeout(200)
      return this.fetchStadiumOverviewReport(...args)
    },
  )

  fetchBooksReadOverviewReportTask = restartableTask(
    async (...args: Parameters<typeof this.fetchBooksReadOverviewReport>) => {
      await timeout(200)
      return this.fetchBooksReadOverviewReport(...args)
    },
  )

  fetchBooksReadEventsReportTask = restartableTask(
    async (...args: Parameters<typeof this.fetchBooksReadEventsReport>) => {
      await timeout(200)
      return this.fetchBooksReadEventsReport(...args)
    },
  )

  fetchStadiumOverviewReport(product: Product, period: NormalizedInterval, requestScope: RequestScope) {
    const reportName = 'student-dashboard/stadium/overview'

    const url = this.reporting.buildGravityUrl({ product, requestScope, reportName })

    const query = this.reporting.buildQuery({ period })

    return this.reporting.fetchReport<StadiumOverviewReportData>(url, query)
  }

  fetchAwardsOverviewReport(product: Product, period: NormalizedInterval, requestScope: RequestScope) {
    const reportName = 'student-dashboard/awards/overview'

    const url = this.reporting.buildGravityUrl({ product, requestScope, reportName })

    const query = this.reporting.buildQuery({ period })

    return this.reporting.fetchReport<AwardsOverviewReportData>(url, query)
  }

  fetchProgressByCategoryOverviewReport(product: Product, period: NormalizedInterval, requestScope: RequestScope) {
    const reportName = 'student-dashboard/progress-by-category/overview'

    const url = this.reporting.buildGravityUrl({ product, requestScope, reportName })

    const query = this.reporting.buildQuery({ period })

    return this.reporting.fetchReport<ProgressByCategoryOverviewReportData>(url, query)
  }

  fetchLexileGrowthOverviewReport(product: Product, period: NormalizedInterval, requestScope: RequestScope) {
    const reportName = 'student-dashboard/lexile-growth/overview'

    const url = this.reporting.buildGravityUrl({ product, requestScope, reportName })

    const query = this.reporting.buildQuery({ period })

    return this.reporting.fetchReport<LexileGrowthOverviewReportData>(url, query)
  }

  fetchComprehensionProgressOverviewReport(product: Product, period: NormalizedInterval, requestScope: RequestScope) {
    const reportName = 'student-dashboard/comprehension-lesson-progress/overview'

    const url = this.reporting.buildGravityUrl({ product, requestScope, reportName })

    const query = this.reporting.buildQuery({ period })

    return this.reporting.fetchReport<ComprehensionProgressOverviewReportData>(url, query)
  }

  fetchComprehensionProgressEventsReport(
    product: Product,
    sort: ReportingQuerySort,
    page: ReportingQueryPage,
    filter: ReportingQueryFilter,
    period: NormalizedInterval,
    requestScope: RequestScope,
  ) {
    const reportName = 'student-dashboard/comprehension-lesson-progress/events'

    const url = this.reporting.buildGravityUrl({ product, requestScope, reportName })

    const query = this.reporting.buildQuery({ sort, page, filter, period })

    return this.reporting.fetchReport<ComprehensionProgressEventsReportData>(url, query)
  }

  fetchAssessmentsOverviewReport(product: Product, period: NormalizedInterval, requestScope: RequestScope) {
    const reportName = 'student-dashboard/assessments/overview'

    const url = this.reporting.buildGravityUrl({ product, requestScope, reportName })

    const query = this.reporting.buildQuery({ period })

    return this.reporting.fetchReport<AssessmentsOverviewReportData>(url, query)
  }

  fetchAssessmentsEventsReport(
    product: Product,
    sort: ReportingQuerySort,
    page: ReportingQueryPage,
    period: NormalizedInterval,
    requestScope: RequestScope,
  ) {
    const reportName = 'student-dashboard/assessments/events'

    const url = this.reporting.buildGravityUrl({ product, requestScope, reportName })

    const query = this.reporting.buildQuery({ sort, page, period })

    return this.reporting.fetchReport<AssessmentsEventsReportData>(url, query)
  }

  fetchAssignmentsOverviewReport(product: Product, period: NormalizedInterval, requestScope: RequestScope) {
    const reportName = 'student-dashboard/assignments/overview'

    const url = this.reporting.buildGravityUrl({ product, requestScope, reportName })

    const query = this.reporting.buildQuery({ period })

    return this.reporting.fetchReport<AssignmentsOverviewReportData>(url, query)
  }

  fetchAssignmentsEventsReport(
    product: Product,
    sort: ReportingQuerySort,
    page: ReportingQueryPage,
    period: NormalizedInterval,
    requestScope: RequestScope,
  ) {
    const reportName = 'student-dashboard/assignments/events'

    const url = this.reporting.buildGravityUrl({ product, requestScope, reportName })

    const query = this.reporting.buildQuery({ sort, page, period })

    return this.reporting.fetchReport<AssignmentsEventsReportData>(url, query)
  }

  fetchSpellingProgressOverviewReport(product: Product, period: NormalizedInterval, requestScope: RequestScope) {
    const reportName = 'student-dashboard/spelling-lesson-progress/overview'

    const url = this.reporting.buildGravityUrl({ product, requestScope, reportName })

    const query = this.reporting.buildQuery({ period })

    return this.reporting.fetchReport<SpellingProgressOverviewReportData>(url, query)
  }

  fetchSpellingProgressEventsReport(
    product: Product,
    sort: ReportingQuerySort,
    page: ReportingQueryPage,
    period: NormalizedInterval,
    requestScope: RequestScope,
  ) {
    const reportName = 'student-dashboard/spelling-lesson-progress/events'

    const url = this.reporting.buildGravityUrl({ product, requestScope, reportName })

    const query = this.reporting.buildQuery({ sort, page, period })

    return this.reporting.fetchReport<SpellingProgressEventsReportData>(url, query)
  }

  fetchBooksReadOverviewReport(product: Product, period: NormalizedInterval, requestScope: RequestScope) {
    const reportName = 'student-dashboard/books-read/overview'

    const url = this.reporting.buildGravityUrl({ product, requestScope, reportName })

    const query = this.reporting.buildQuery({ period })

    return this.reporting.fetchReport<BooksReadOverviewReportData>(url, query)
  }

  fetchBooksReadEventsReport(
    product: Product,
    sort: ReportingQuerySort,
    page: ReportingQueryPage,
    period: NormalizedInterval,
    requestScope: RequestScope,
  ) {
    const reportName = 'student-dashboard/books-read/events'

    const url = this.reporting.buildGravityUrl({ product, requestScope, reportName })

    const query = this.reporting.buildQuery({ sort, page, period })

    return this.reporting.fetchReport<BooksReadEventsReportData>(url, query)
  }
}
